import Head from "next/head";
import { useRouter } from "next/router";
import DataLayerRuntime from "@/Component/Seo/DataLayerRuntime";
import { isSeoProductionEnv } from "@/utils/env";

export default function SeoHead({
  seo,
  fallbackTitle = "Next Holidays",
  country = "UAE",
  noIndex = false,
}) {
  const router = useRouter();
  const isProduction = isSeoProductionEnv();
  const isIndia = country === "IN";

  if (!isProduction) {
    return (
      <>
        <Head>
          <meta name="robots" content="noindex, nofollow" />
        </Head>
        <DataLayerRuntime dataLayer={seo?.dataLayer} />
      </>
    );
  }

  if (noIndex) {
    return (
      <>
        <Head>
          <title>{seo?.title || fallbackTitle}</title>
          <meta name="robots" content="noindex, nofollow" />
          <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover" />
          <link rel="icon" href="/favicon.ico" />
        </Head>
        <DataLayerRuntime dataLayer={seo?.dataLayer} />
      </>
    );
  }

  if (!seo) {
    return (
      <Head>
        <title>{fallbackTitle}</title>
        <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover" />
        <link rel="icon" href="/favicon.ico" />
      </Head>
    );
  }

  let pathArray = router.asPath.split("/").filter(Boolean);
  if (pathArray[0] === "in") pathArray.shift();
  const baseUrl = `https://www.nextholidays.com${isIndia ? "/in" : ""}`;

  return (
    <>
    <Head>
      <title>{seo.title || fallbackTitle}</title>
      <meta charSet="UTF-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover" />
      <link rel="icon" href="/favicon.ico" />

      <script
        dangerouslySetInnerHTML={{
          __html: `
            (function(){
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              window.gtag = window.gtag || gtag;
              gtag('consent', 'default', {
                ad_storage: 'denied',
                analytics_storage: 'denied',
                ad_user_data: 'denied',
                ad_personalization: 'denied'
              });
              var consentCookie = document.cookie
                .split('; ')
                .find(function(c){ return c.indexOf('cookie_preferences=') === 0; });
              if (consentCookie) {
                var value = decodeURIComponent(consentCookie.split('=')[1]);
                var analyticsGranted = value.indexOf('analytics:yes') !== -1;
                var adsGranted = value.indexOf('advertisement:yes') !== -1;
                gtag('consent', 'update', {
                  analytics_storage: analyticsGranted ? 'granted' : 'denied',
                  ad_storage: adsGranted ? 'granted' : 'denied',
                  ad_user_data: adsGranted ? 'granted' : 'denied',
                  ad_personalization: adsGranted ? 'granted' : 'denied'
                });
              }
            })();
          `,
        }}
      />

      {Array.isArray(seo.tags) &&
        seo.tags.map((tag, index) => {
          if (tag.tag === "meta") {
            const httpEquiv = String(
              tag.attrs?.["http-equiv"] || tag.attrs?.httpEquiv || ""
            ).toLowerCase();
            if (httpEquiv === "refresh") return null;
            return <meta key={`meta-${index}`} {...tag.attrs} />;
          }
          if (tag.tag === "link") {
            return <link key={`link-${index}`} {...tag.attrs} />;
          }
          if (tag.tag === "script") {
            if (
              /location\s*\.\s*(reload|replace)|http-equiv\s*=\s*['"]refresh/i.test(
                String(tag.content || "")
              )
            ) {
              return null;
            }
            return (
              <script
                key={`script-${index}`}
                type={tag.type}
                dangerouslySetInnerHTML={{ __html: tag.content }}
              />
            );
          }
          return null;
        })}

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: baseUrl,
              },
              ...pathArray.map((segment, index) => ({
                "@type": "ListItem",
                position: index + 2,
                name: segment
                  .split("?")[0]
                  .replace(/-/g, " ")
                  .replace(/\b\w/g, (char) => char.toUpperCase()),
                item: `${baseUrl}/${pathArray.slice(0, index + 1).join("/")}`,
              })),
            ],
          }),
        }}
      />

      <meta httpEquiv="content-language" content={isIndia ? "en-in" : "en-ae"} />
      <meta
        name="google-site-verification"
        content="8fXAaMTLGORUaqgXODKUrzACFbeddnqs1DjpRjNafVM"
      />

      {isIndia ? (
        <script
          id="gtm-IN"
          dangerouslySetInnerHTML={{
            __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
              new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
              j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
              'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','GTM-55LM8988');`,
          }}
        />
      ) : (
        <script
          id="gtm-OTHER"
          dangerouslySetInnerHTML={{
            __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
              new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
              j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
              'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','GTM-595GMXLK');`,
          }}
        />
      )}

      <script
        dangerouslySetInnerHTML={{
          __html: `
            (function(c,l,a,r,i,t,y){
              c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
              t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
              y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
            })(window, document, "clarity", "script", "${isIndia ? "nt77a36ndw" : "qjxbaj3o6g"}");
          `,
        }}
      />

      {isIndia ? (
        <>
          <script
            id="facebook-pixel"
            dangerouslySetInnerHTML={{
              __html: `
                !function(f,b,e,v,n,t,s)
                {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                n.queue=[];t=b.createElement(e);t.async=!0;
                t.src=v;s=b.getElementsByTagName(e)[0];
                s.parentNode.insertBefore(t,s)}(window, document,'script',
                'https://connect.facebook.net/en_US/fbevents.js');
                fbq('init', '1118965902962661');
                fbq('track', 'PageView');
              `,
            }}
          />
          <noscript>
            <img
              height="1"
              width="1"
              style={{ display: "none" }}
              src="https://www.facebook.com/tr?id=1118965902962661&ev=PageView&noscript=1"
              alt=""
            />
          </noscript>
        </>
      ) : null}
    </Head>
    <DataLayerRuntime dataLayer={seo.dataLayer} />
    </>
  );
}
